/**
 * Full-screen image gallery, shared by the Notes drawer, the Trade
 * Journal drawer and the Image Board.
 *
 * Both drawers previously carried their own one-shot lightbox —
 * open one image, Escape to close, no way to reach the next one.
 * This replaces both. The caller passes the whole set plus the index
 * to open at, so the Board can hand over every image it is showing
 * and the arrows walk across notes, not just within one.
 *
 * The caller resolves URLs: notes serve attachments from
 * /api/notes/<id>/attachments/<att>, the journal from
 * /api/journal/attachments/<att>, and there is no shape that covers
 * both worth inventing.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./ImageLightbox.module.css";

export interface GalleryImage {
  /** Attachment id — also the React key. */
  id: string;
  url: string;
  /** Resolved display label ("2", "entry"). Callers fall back to the
   *  position for attachments the server hasn't backfilled yet. */
  label: string;
  filename: string;
  /** Owning note title / trade summary, shown under the label. */
  context?: string | null;
}

export function ImageLightbox({
  items,
  startIndex,
  onClose,
  onEdit,
  onDelete,
}: {
  items: GalleryImage[];
  startIndex: number;
  onClose: () => void;
  onEdit?: (item: GalleryImage) => void;
  onDelete?: (item: GalleryImage) => void;
}) {
  const [index, setIndex] = useState(startIndex);
  // Only used to decide whether a set change should re-seed the
  // index (a different gallery) or clamp it (a delete from this one).
  const lastStart = useRef(startIndex);

  // Re-seed when the caller opens a different image without
  // unmounting us — the Board does exactly that when the context
  // menu's "Open" runs while a lightbox is already up.
  useEffect(() => {
    if (lastStart.current !== startIndex) {
      lastStart.current = startIndex;
      setIndex(startIndex);
    }
  }, [startIndex]);

  // Deleting the last image in the set leaves `index` past the end.
  // Clamp rather than render a broken <img>, and close outright once
  // nothing is left.
  useEffect(() => {
    if (items.length === 0) {
      onClose();
      return;
    }
    setIndex((i) => Math.min(Math.max(i, 0), items.length - 1));
  }, [items.length, onClose]);

  const count = items.length;
  const go = useCallback(
    (delta: number) => {
      // Wrap — a gallery this small has no useful dead end, and
      // stopping at the edges just feels broken when arrowing fast.
      setIndex((i) => (count === 0 ? 0 : (i + delta + count) % count));
    },
    [count],
  );

  // Capture phase + stopPropagation: the Notes and Journal drawers
  // both close themselves on a bubbling Escape, so without this one
  // Escape would dismiss the image AND the drawer behind it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          e.stopPropagation();
          onClose();
          break;
        case "ArrowLeft":
          e.stopPropagation();
          go(-1);
          break;
        case "ArrowRight":
          e.stopPropagation();
          go(1);
          break;
        case "Home":
          e.stopPropagation();
          setIndex(0);
          break;
        case "End":
          e.stopPropagation();
          setIndex(count - 1);
          break;
        default:
          return;
      }
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [go, onClose, count]);

  const current = items[index];

  // Warm the neighbours. A chart screenshot is routinely 2500px
  // wide; without this, arrowing shows a blank frame while the next
  // one decodes even though it is already in the HTTP cache.
  useEffect(() => {
    if (count < 2) return;
    for (const delta of [1, -1]) {
      const neighbour = items[(index + delta + count) % count];
      if (!neighbour) continue;
      const img = new Image();
      img.src = neighbour.url;
    }
  }, [index, items, count]);

  if (!current) return null;

  return (
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Image ${index + 1} of ${count}`}
    >
      <div className={styles.stage} onClick={(e) => e.stopPropagation()}>
        <img
          className={styles.image}
          src={current.url}
          alt={current.filename}
        />
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            className={`${styles.nav} ${styles.navPrev}`}
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            title="Previous image (←)"
            aria-label="Previous image"
          >
            ‹
          </button>
          <button
            type="button"
            className={`${styles.nav} ${styles.navNext}`}
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            title="Next image (→)"
            aria-label="Next image"
          >
            ›
          </button>
        </>
      )}

      <div className={styles.bar} onClick={(e) => e.stopPropagation()}>
        <span className={styles.label}>#{current.label}</span>
        {current.context && (
          <span className={styles.context}>{current.context}</span>
        )}
        <span className={styles.filename}>{current.filename}</span>
        <span className={styles.spacer} />
        {onEdit && (
          <button
            type="button"
            className={styles.barBtn}
            onClick={() => onEdit(current)}
            title="Annotate this image"
          >
            ✎ Annotate
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            className={`${styles.barBtn} ${styles.barBtnDanger}`}
            onClick={() => onDelete(current)}
            title="Delete this image"
          >
            Delete
          </button>
        )}
        <span className={styles.counter}>
          {index + 1} / {count}
        </span>
        <button
          type="button"
          className={styles.barBtn}
          onClick={onClose}
          title="Close (Esc)"
          aria-label="Close"
        >
          ×
        </button>
      </div>
    </div>
  );
}
